// Validation for user-imported audio files (e.g. Zoom/Teams recordings pulled off disk),
// picked via the AppShell "Import" button. Kept DOM-light and pure so it's unit-testable
// without mounting a file <input>. See App.tsx's Shell for the file-picker wiring that
// calls validateImportFile() before handing the File to useUploadQueue.enqueue (the
// @scribe/core queue treats a File exactly like the Blobs RecordScreen records — no
// special-casing needed there).

// Browsers are inconsistent about what `file.type` reports for less common audio formats
// (notably .m4a/.aac report '' or an odd vendor MIME type on some platforms), so
// ACCEPTED_EXTENSIONS below is used as a fallback whenever `file.type` doesn't match.
export const ACCEPTED_AUDIO_MIME_TYPES = [
  'audio/mpeg',
  'audio/mp3',
  'audio/wav',
  'audio/wave',
  'audio/x-wav',
  'audio/mp4',
  'audio/x-m4a',
  'audio/m4a',
  'audio/aac',
  'audio/ogg',
  'audio/webm',
  'audio/flac',
  'audio/x-flac',
];

const ACCEPTED_EXTENSIONS = [
  '.mp3',
  '.wav',
  '.wave',
  '.m4a',
  '.aac',
  '.ogg',
  '.oga',
  '.webm',
  '.flac',
  '.aiff',
  '.aif',
];

/** Value for the file input's `accept` attribute: MIME types plus extensions, so the OS
 * file picker's default filter stays generous even on platforms that filter by extension
 * rather than MIME type. */
export const IMPORT_ACCEPT_ATTR = [...ACCEPTED_AUDIO_MIME_TYPES, ...ACCEPTED_EXTENSIONS].join(',');

// 2GB comfortably covers a many-hours compressed meeting recording while still catching an
// accidental video-file selection or other mis-click. S3 multipart itself has a far higher
// ceiling (10,000 parts) — this is purely a client-side sanity check, not a protocol limit.
export const MAX_IMPORT_SIZE_BYTES = 2 * 1024 * 1024 * 1024;

function hasAcceptedExtension(filename: string): boolean {
  const lower = filename.toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

/** Returns a user-facing error message if `file` fails type/size validation, or `null` if
 * it's fine to import. */
export function validateImportFile(file: File): string | null {
  const typeOk = ACCEPTED_AUDIO_MIME_TYPES.includes(file.type.toLowerCase());
  if (!typeOk && !hasAcceptedExtension(file.name)) {
    return `"${file.name}" doesn't look like an audio file. Choose an mp3, wav, m4a, aac, ogg, webm, or flac recording.`;
  }
  if (file.size === 0) {
    return `"${file.name}" is empty.`;
  }
  if (file.size > MAX_IMPORT_SIZE_BYTES) {
    const maxGb = Math.round(MAX_IMPORT_SIZE_BYTES / (1024 * 1024 * 1024));
    return `"${file.name}" is too large to import (max ${maxGb} GB).`;
  }
  return null;
}

/** Title to seed the imported Recording with — the filename minus its extension, falling
 * back to a timestamped placeholder if the name is empty or all-extension (e.g. ".mp3"). */
export function titleFromFilename(filename: string): string {
  const base = filename.replace(/\.[^./\\]+$/, '').trim();
  return base || `Imported recording ${new Date().toLocaleString()}`;
}
